import React, { useState, useEffect } from 'react';
import api, { uploadFile } from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit3, Trash2, Users, X, Upload, Globe, Instagram, Youtube, Facebook, Music2, Image, Search, ExternalLink, Music } from 'lucide-react';

// ─── Modal creazione/modifica artista (stessi campi del frontend utente) ──────
function AdminArtistModal({ artist, userId, onClose, onSaved }) {
  const [form, setForm] = useState(artist
    ? { name: artist.name || '', spotify_uri: artist.spotify_uri || '', apple_music_id: artist.apple_music_id || '',
        photo_path: artist.photo_path || '', biography: artist.biography || '',
        link_instagram: artist.link_instagram || '', link_facebook: artist.link_facebook || '',
        link_youtube: artist.link_youtube || '', link_tiktok: artist.link_tiktok || '',
        link_website: artist.link_website || '', link_twitter: artist.link_twitter || '' }
    : { name: '', spotify_uri: '', apple_music_id: '', photo_path: '', biography: '',
        link_instagram: '', link_facebook: '', link_youtube: '', link_tiktok: '', link_website: '', link_twitter: '' }
  );
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(artist?.photo_path ? `/uploads/artwork/${userId}/${artist.photo_path}` : null);
  const [uploading, setUploading] = useState(false);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(`/uploads/artwork?for_user=${userId}`, f, 'artwork');
      setForm(p => ({ ...p, photo_path: data.file.filename }));
      setPhotoPreview(URL.createObjectURL(f));
      toast.success('Foto caricata');
    } catch (err) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Nome artista obbligatorio');
    setSaving(true);
    try {
      if (artist?.id) {
        await api.put(`/artists/admin/${artist.id}`, form);
        toast.success('Artista aggiornato!');
      } else {
        await api.post(`/artists/admin/for-user/${userId}`, form);
        toast.success('Artista creato!');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-white">{artist?.id ? 'Modifica Artista' : 'Nuovo Artista'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded text-gray-400"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          {/* Photo */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-700 border-2 border-dashed border-gray-600 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {uploading
                ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400" />
                : photoPreview
                  ? <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                  : <Image size={24} className="text-gray-500" />}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="adm-artist-photo" onChange={handlePhoto} />
              <label htmlFor="adm-artist-photo"
                className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-600 inline-flex items-center gap-1.5 text-xs">
                <Upload size={12} />Foto Artista
              </label>
              <p className="text-[10px] text-gray-500 mt-1">JPG/PNG, quadrata, 500×500px</p>
              {photoPreview && (
                <button onClick={() => { setForm(p => ({ ...p, photo_path: '' })); setPhotoPreview(null); }}
                  className="text-[10px] text-red-400 hover:underline mt-1">Rimuovi foto</button>
              )}
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Nome Artista *</label>
            <input className="input-field-dark" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus />
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Biografia</label>
            <textarea className="input-field-dark h-20 resize-y" value={form.biography}
              onChange={e => setForm(p => ({ ...p, biography: e.target.value }))} placeholder="Breve biografia..." />
          </div>

          {/* Piattaforme */}
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-3">Piattaforme Musicali</p>
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Spotify URI</label>
                <input className="input-field-dark" value={form.spotify_uri}
                  onChange={e => setForm(p => ({ ...p, spotify_uri: e.target.value }))} placeholder="spotify:artist:4Z8W4fK..." />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Apple Music ID</label>
                <input className="input-field-dark" value={form.apple_music_id}
                  onChange={e => setForm(p => ({ ...p, apple_music_id: e.target.value }))} placeholder="https://music.apple.com/artist/..." />
              </div>
            </div>
          </div>

          {/* Social & Link */}
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-3">Link & Social</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Globe size={14} className="text-gray-500 flex-shrink-0" /><input className="input-field-dark" value={form.link_website} onChange={e => setForm(p => ({ ...p, link_website: e.target.value }))} placeholder="https://..." /></div>
              <div className="flex items-center gap-2"><Instagram size={14} className="text-gray-500 flex-shrink-0" /><input className="input-field-dark" value={form.link_instagram} onChange={e => setForm(p => ({ ...p, link_instagram: e.target.value }))} placeholder="https://instagram.com/..." /></div>
              <div className="flex items-center gap-2"><Facebook size={14} className="text-gray-500 flex-shrink-0" /><input className="input-field-dark" value={form.link_facebook} onChange={e => setForm(p => ({ ...p, link_facebook: e.target.value }))} placeholder="https://facebook.com/..." /></div>
              <div className="flex items-center gap-2"><Youtube size={14} className="text-gray-500 flex-shrink-0" /><input className="input-field-dark" value={form.link_youtube} onChange={e => setForm(p => ({ ...p, link_youtube: e.target.value }))} placeholder="https://youtube.com/@..." /></div>
              <div className="flex items-center gap-2"><Music2 size={14} className="text-gray-500 flex-shrink-0" /><input className="input-field-dark" value={form.link_tiktok} onChange={e => setForm(p => ({ ...p, link_tiktok: e.target.value }))} placeholder="https://tiktok.com/@..." /></div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Annulla</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 disabled:opacity-50 text-sm">
            {saving ? 'Salvataggio...' : artist?.id ? 'Salva Modifiche' : 'Crea Artista'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function AdminArtistsPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, artist: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/users?limit=500');
        setUsers((data.users || []).filter(u => u.role !== 'admin'));
      } catch (e) {}
    })();
  }, []);

  const loadArtists = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try { const { data } = await api.get(`/artists/admin/by-user/${selectedUser}`); setArtists(data.artists || []); }
    catch (e) { setArtists([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (selectedUser) loadArtists(); else setArtists([]); }, [selectedUser]);

  const deleteArtist = async (id, name) => {
    if (!window.confirm(`Eliminare l'artista "${name}"?`)) return;
    try {
      await api.delete(`/artists/admin/${id}`);
      toast.success('Artista eliminato');
      loadArtists();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore eliminazione'); }
  };

  const filtered = search ? artists.filter(a => a.name.toLowerCase().includes(search.toLowerCase())) : artists;
  const selectedUserObj = users.find(u => String(u.id) === selectedUser);

  return (
    <div>
      {modal.open && (
        <AdminArtistModal
          artist={modal.artist}
          userId={selectedUser}
          onClose={() => setModal({ open: false, artist: null })}
          onSaved={() => { setModal({ open: false, artist: null }); loadArtists(); }}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Gestione Artisti</h1>
        {selectedUser && (
          <button onClick={() => setModal({ open: true, artist: null })}
            className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 inline-flex items-center gap-2 text-sm">
            <Plus size={16} />Nuovo Artista
          </button>
        )}
      </div>

      <div className="flex gap-3 mb-6">
        <select className="input-field-dark flex-1" value={selectedUser} onChange={e => { setSelectedUser(e.target.value); setSearch(''); }}>
          <option value="">Seleziona un utente...</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
        </select>
        {selectedUser && artists.length > 3 && (
          <div className="relative w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input className="input-field-dark pl-10" placeholder="Cerca artista..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        )}
      </div>

      {!selectedUser ? (
        <div className="card-dark p-12 text-center text-gray-500">
          <Users size={48} className="mx-auto mb-4 opacity-30" />
          <p>Seleziona un utente per vedere i suoi artisti</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" /></div>
      ) : (
        <div>
          <p className="text-sm text-gray-400 mb-4">
            Artisti di <strong className="text-white">{selectedUserObj?.first_name} {selectedUserObj?.last_name}</strong> ({filtered.length})
          </p>

          {filtered.length === 0 ? (
            <div className="card-dark p-8 text-center text-gray-500">
              <Music size={32} className="mx-auto mb-3 opacity-30" />
              <p>Nessun artista per questo utente</p>
              <button onClick={() => setModal({ open: true, artist: null })}
                className="mt-3 text-xs text-yellow-400 hover:underline">+ Crea il primo artista</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(a => (
                <div key={a.id} className="card-dark p-5 group hover:border-gray-600 transition">
                  <div className="flex items-start gap-3">
                    {/* Photo */}
                    {a.photo_path
                      ? <img src={`/uploads/artwork/${a.user_id}/${a.photo_path}`} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                      : <div className="w-12 h-12 rounded-full bg-gray-700 text-yellow-400 flex items-center justify-center text-sm font-bold flex-shrink-0">{a.name?.[0]}</div>}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate">{a.name}</h3>
                      {a.biography && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{a.biography}</p>}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {a.spotify_uri && <span className="text-[9px] bg-green-900/40 text-green-400 px-1.5 py-0.5 rounded">Spotify ✓</span>}
                        {a.apple_music_id && <span className="text-[9px] bg-pink-900/40 text-pink-400 px-1.5 py-0.5 rounded">Apple ✓</span>}
                      </div>
                    </div>
                  </div>

                  {/* Social badges */}
                  {(a.link_website || a.link_instagram || a.link_facebook || a.link_youtube || a.link_tiktok) && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {a.link_website && <a href={a.link_website} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white"><Globe size={12} /></a>}
                      {a.link_instagram && <a href={a.link_instagram} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-pink-400"><Instagram size={12} /></a>}
                      {a.link_facebook && <a href={a.link_facebook} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-blue-400"><Facebook size={12} /></a>}
                      {a.link_youtube && <a href={a.link_youtube} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-red-400"><Youtube size={12} /></a>}
                      {a.link_tiktok && <a href={a.link_tiktok} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white"><Music2 size={12} /></a>}
                    </div>
                  )}

                  {/* Platform links */}
                  {(a.spotify_uri || a.apple_music_id) && (
                    <div className="flex gap-3 mt-2">
                      {a.spotify_uri && <a href={`https://open.spotify.com/artist/${a.spotify_uri.replace('spotify:artist:','')}`} target="_blank" rel="noreferrer" className="text-[10px] text-green-400 hover:underline flex items-center gap-1"><ExternalLink size={9} />Spotify</a>}
                      {a.apple_music_id && <a href={a.apple_music_id.startsWith('http') ? a.apple_music_id : '#'} target="_blank" rel="noreferrer" className="text-[10px] text-pink-400 hover:underline flex items-center gap-1"><ExternalLink size={9} />Apple</a>}
                    </div>
                  )}

                  {/* Azioni */}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-700">
                    <span className="text-[10px] text-gray-600">{a.usage_count || 0} utilizzi</span>
                    <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => setModal({ open: true, artist: a })}
                        className="text-xs text-yellow-400 hover:underline inline-flex items-center gap-1">
                        <Edit3 size={12} />Modifica
                      </button>
                      <button onClick={() => deleteArtist(a.id, a.name)}
                        className="text-xs text-red-400 hover:underline inline-flex items-center gap-1">
                        <Trash2 size={12} />Elimina
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
